import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { RotateCcw, CheckCircle, XCircle, Target } from 'lucide-react';
import { Resource, UIComponent } from '../../types';

interface SpatialCanvasComponentProps {
  resource: Resource;
  component: UIComponent;
  onAction?: (action: string, data?: any) => void;
}

interface SpatialObject {
  id: string;
  type: string;
  position: string;
  color: string;
  x: number;
  y: number;
  isPlaced: boolean;
}

interface GridCell {
  x: number;
  y: number;
  position: string;
  occupied: boolean;
  objectId: string | null;
}

export const SpatialCanvasComponent: React.FC<SpatialCanvasComponentProps> = ({ 
  resource, 
  component, 
  onAction 
}) => {
  const [grid, setGrid] = useState<GridCell[][]>([]);
  const [objects, setObjects] = useState<SpatialObject[]>([]);
  const [selectedObject, setSelectedObject] = useState<SpatialObject | null>(null);
  const [currentInstruction, setCurrentInstruction] = useState<string>('');
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [feedbackType, setFeedbackType] = useState<'success' | 'error'>('success');
  const [instructionIndex, setInstructionIndex] = useState(0);

  // Get configuration from resource
  const gridSize = resource.gridSize || '5x5';
  const [rows, cols] = gridSize.split('x').map(Number);
  
  // Responsive cell size calculation
  const [containerRef, setContainerRef] = useState<HTMLDivElement | null>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  
  // Calculate responsive cell size based on container width
  const cellSize = useMemo(() => {
    if (containerWidth === 0) return 40; // Default size
    
    // Calculate available width (subtract padding and object panel width)
    const availableWidth = Math.max(containerWidth - 200, 200); // 200px for object panel
    const maxCellSize = Math.min(availableWidth / cols, 60); // Max 60px per cell
    const minCellSize = 25; // Min 25px per cell
    
    return Math.max(minCellSize, Math.min(maxCellSize, 60));
  }, [containerWidth, cols]);
  
  const canvasWidth = cols * cellSize;
  const canvasHeight = rows * cellSize;
  
  // Get settings from resource
  const showGrid = resource.showGrid !== undefined ? resource.showGrid : true;
  const allowInteraction = resource.allowInteraction !== undefined ? resource.allowInteraction : true;
  const question = resource.question || 'Placez les objets selon les consignes spatiales';
  
  // Get objects and directions from resource
  
  


  const generateGrid = useCallback(() => {
    const getPositionFromCoordinates = (x: number, y: number): string => {
      const centerX = Math.floor(cols / 2);
      const centerY = Math.floor(rows / 2);
      
      if (x === centerX && y === centerY) return 'center';
      if (x < centerX) return 'gauche';
      if (x > centerX) return 'droite';
      if (y < centerY) return 'haut';
      if (y > centerY) return 'bas';
      return 'center';
    };
    const newGrid: GridCell[][] = [];
    
    for (let y = 0; y < rows; y++) {
      const row: GridCell[] = [];
      for (let x = 0; x < cols; x++) {
        const position = getPositionFromCoordinates(x, y);
        row.push({
          x,
          y,
          position,
          occupied: false,
          objectId: null
        });
      }
      newGrid.push(row);
    }
    
    setGrid(newGrid);
  }, [rows, cols]);


  const generateObjects = useCallback(() => {
    const objectConfigs: SpatialObject[] = resource.objects || [];
    const newObjects: SpatialObject[] = objectConfigs.map((config, index) => ({
      ...config,
      id: config.id || `object_${index}`,
      x: -1,
      y: -1,
      isPlaced: false
    }));
    
    setObjects(newObjects);
  }, [resource.objects]);

  const generateInstructions = useCallback(() => {
    if (objects.length === 0) return;
    
    const instructions = objects.map((obj, index) => {
      const position = obj.position;
      const color = obj.color;
      const type = obj.type;
      
      switch (position) {
        case 'center':
          return `Placez le ${type} ${color} au centre`;
        case 'left':
          return `Placez le ${type} ${color} à gauche`;
        case 'right':
          return `Placez le ${type} ${color} à droite`;
        case 'top':
          return `Placez le ${type} ${color} en haut`;
        case 'bottom':
          return `Placez le ${type} ${color} en bas`;
        case 'top-left':
          return `Placez le ${type} ${color} en haut à gauche`;
        case 'top-right':
          return `Placez le ${type} ${color} en haut à droite`;
        case 'bottom-left':
          return `Placez le ${type} ${color} en bas à gauche`;
        case 'bottom-right':
          return `Placez le ${type} ${color} en bas à droite`;
        default:
          return `Placez le ${type} ${color}`;
      }
    });
    
    if (instructions.length > 0) {
      setCurrentInstruction(instructions[instructionIndex] || instructions[0]);
    }
  }, [objects, instructionIndex]);

  const handleCellClick = (cell: GridCell) => {
    if (!selectedObject || !allowInteraction) return;
    
    // Check if cell is occupied
    if (cell.occupied) {
      showFeedbackMessage('Cette position est déjà occupée !', 'error');
      return;
    }
    
    // Place object
    const updatedObjects = objects.map(obj => 
      obj.id === selectedObject.id 
        ? { ...obj, x: cell.x, y: cell.y, isPlaced: true }
        : obj
    );
    
    const updatedGrid = grid.map(row => 
      row.map(c => 
        c.x === cell.x && c.y === cell.y
          ? { ...c, occupied: true, objectId: selectedObject.id }
          : c
      )
    );
    
    setObjects(updatedObjects);
    setGrid(updatedGrid);
    setSelectedObject(null);
    
    // Validate the placement
    const isCorrect = validatePlacement(selectedObject, cell);
    if (isCorrect) {
      showFeedbackMessage('Objet placé correctement !', 'success');
    } else {
      showFeedbackMessage(`Objet placé, mais la position correcte était "${selectedObject.position}"`, 'error');
    }
    
    onAction?.('object_placed', { 
      objectId: selectedObject.id, 
      position: cell.position, 
      expectedPosition: selectedObject.position,
      isCorrect,
      x: cell.x, 
      y: cell.y 
    });
    
    // Move to next instruction
    if (instructionIndex < objects.length - 1) {
      setInstructionIndex(prev => prev + 1);
      // setTimeout(() => {
      //   generateInstructions();
      // }, 1000);
    }
  };

  const handleObjectSelect = (object: SpatialObject) => {
    if (!allowInteraction) return;
    setSelectedObject(object);
  };

  const showFeedbackMessage = (message: string, type: 'success' | 'error') => {
    setFeedbackMessage(message);
    setFeedbackType(type);
    setShowFeedback(true);
    
    setTimeout(() => {
      setShowFeedback(false);
    }, 2000);
  };

  const resetCanvas = () => {
    generateGrid();
    generateObjects();
    setInstructionIndex(0);
    setSelectedObject(null);
    onAction?.('canvas_reset', {});
  };

  const getPlacedCount = () => {
    return objects.filter(obj => obj.isPlaced).length;
  };

  const getTotalCount = () => {
    return objects.length;
  };

  // Position mapping for validation (English to French and variations)
  const positionMapping: Record<string, string[]> = {
    'left': ['gauche', 'left', 'g'],
    'right': ['droite', 'right', 'd'],
    'top': ['haut', 'top', 'h'],
    'bottom': ['bas', 'bottom', 'b'],
    'center': ['centre', 'center', 'c', 'middle'],
    'top-left': ['haut-gauche', 'top-left', 'top-left', 'haut-g', 'h-g'],
    'top-right': ['haut-droite', 'top-right', 'top-right', 'haut-d', 'h-d'],
    'bottom-left': ['bas-gauche', 'bottom-left', 'bottom-left', 'bas-g', 'b-g'],
    'bottom-right': ['bas-droite', 'bottom-right', 'bottom-right', 'bas-d', 'b-d']
  };

  // Helper function to normalize position names
  const normalizePosition = (position: string): string => {
    const normalized = position.toLowerCase().trim();
    
    // Handle common variations
    if (normalized.includes('gauche') || normalized.includes('left')) return 'gauche';
    if (normalized.includes('droite') || normalized.includes('right')) return 'droite';
    if (normalized.includes('haut') || normalized.includes('top')) return 'haut';
    if (normalized.includes('bas') || normalized.includes('bottom')) return 'bas';
    if (normalized.includes('centre') || normalized.includes('center') || normalized.includes('middle')) return 'centre';
    
    // Handle compound positions
    if (normalized.includes('haut-gauche') || normalized.includes('top-left')) return 'haut-gauche';
    if (normalized.includes('haut-droite') || normalized.includes('top-right')) return 'haut-droite';
    if (normalized.includes('bas-gauche') || normalized.includes('bottom-left')) return 'bas-gauche';
    if (normalized.includes('bas-droite') || normalized.includes('bottom-right')) return 'bas-droite';
    
    return position; // Return original if no match
  };

  // Validation functions
  const validatePlacement = (object: SpatialObject, cell: GridCell): boolean => {
    // Check if the object is placed in the correct position according to its configuration
    const expectedPositions = positionMapping[object.position] || [object.position];
    
    // Normalize both positions for comparison
    const normalizedObjectPosition = normalizePosition(object.position);
    const normalizedCellPosition = normalizePosition(cell.position);
    
    // Check if positions match after normalization
    const isCorrect = expectedPositions.some(expected => 
      normalizePosition(expected) === normalizedCellPosition
    ) || normalizedObjectPosition === normalizedCellPosition;    
    return isCorrect;
  };

  const getValidationScore = (): { correct: number; total: number; percentage: number } => {
    const placedObjects = objects.filter(obj => obj.isPlaced);
    const correctPlacements = placedObjects.filter(obj => {
      const cell = grid.flat().find(c => c.objectId === obj.id);
      return cell && validatePlacement(obj, cell);
    });
    
    return {
      correct: correctPlacements.length,
      total: placedObjects.length,
      percentage: placedObjects.length > 0 ? Math.round((correctPlacements.length / placedObjects.length) * 100) : 0
    };
  };

  const validateAllPlacements = () => {
    const score = getValidationScore();
    const allCorrect = score.correct === score.total && score.total === objects.length;
    
    if (allCorrect) {
      showFeedbackMessage(`Félicitations ! Tous les objets sont correctement placés (${score.percentage}%)`, 'success');
      onAction?.('validation_complete', { score, allCorrect: true });
    } else {
      showFeedbackMessage(`Validation : ${score.correct}/${score.total} objets correctement placés (${score.percentage}%)`, 'success');
      onAction?.('validation_partial', { score, allCorrect: false });
    }
  };

  // Track container width for responsive sizing
  useEffect(() => {
    if (containerRef) {
      const updateWidth = () => {
        if (containerRef) {
          setContainerWidth(containerRef.offsetWidth);
        }
      };
      
      updateWidth();
      window.addEventListener('resize', updateWidth);
      
      return () => window.removeEventListener('resize', updateWidth);
    }
  }, [containerRef]);

  useEffect(() => {
    generateGrid();
    generateObjects();
  }, [generateGrid, generateObjects]);

  useEffect(() => {
    generateInstructions();
  }, [generateInstructions]);

  return (
    <div 
      ref={setContainerRef}
      className="spatial-canvas-component p-4 bg-white rounded-lg border border-gray-200 w-full"
    >
      {/* Header */}
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          {resource.name || 'Canvas Spatial'}
        </h3>
        <p className="text-sm text-gray-600 mb-2">
          {resource.description || 'Exercices de positionnement dans l\'espace'}
        </p>
        {question && (
          <p className="text-md font-medium text-blue-600 mb-2">
            {question}
          </p>
        )}
        <div className="flex items-center space-x-4 text-sm text-gray-600">
          <span>Objets placés: {getPlacedCount()}/{getTotalCount()}</span>
          <button
            onClick={validateAllPlacements}
            className="flex items-center space-x-1 text-green-600 hover:text-green-800"
            disabled={getPlacedCount() === 0}
          >
            <CheckCircle className="w-4 h-4" />
            <span>Valider</span>
          </button>
          <button
            onClick={resetCanvas}
            className="flex items-center space-x-1 text-blue-600 hover:text-blue-800"
          >
            <RotateCcw className="w-4 h-4" />
            <span>Réinitialiser</span>
          </button>
        </div>
      </div>

      {/* Current Instruction */}
      {currentInstruction && (
        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div className="flex items-center space-x-2">
            <Target className="w-5 h-5 text-yellow-600" />
            <span className="font-medium text-yellow-800">{currentInstruction}</span>
          </div>
        </div>
      )}

      {/* Validation Summary */}
      {getPlacedCount() > 0 && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <CheckCircle className="w-5 h-5 text-blue-600" />
              <span className="font-medium text-blue-800">Progression</span>
            </div>
            <div className="text-sm text-blue-700">
              {(() => {
                const score = getValidationScore();
                return `${score.correct}/${score.total} corrects (${score.percentage}%)`;
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Canvas */}
      {objects.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <Target className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <p className="text-lg font-medium">Aucun objet configuré</p>
          <p className="text-sm">Veuillez configurer des objets dans les paramètres de la ressource</p>
        </div>
      ) : (
        <>
          {/* Grid */}
          <div className="flex justify-center mb-6">
          <div 
            className="border-2 border-gray-300 rounded-lg bg-white relative"
            style={{ width: canvasWidth, height: canvasHeight }}
          >
            {grid.map((row, rowIndex) => 
              row.map((cell, colIndex) => (
                <div
                  key={`${rowIndex}-${colIndex}`}
                  className={`absolute border border-gray-200 cursor-pointer transition-colors ${
                    cell.occupied 
                      ? (() => {
                          const object = objects.find(obj => obj.id === cell.objectId);
                          if (object && validatePlacement(object, cell)) {
                            return 'bg-green-100 border-green-300'; // Correct placement
                          } else if (object) {
                            return 'bg-red-100 border-red-300'; // Incorrect placement
                          }
                          return 'bg-blue-100 border-blue-300'; // Default occupied
                        })()
                      : selectedObject 
                        ? 'hover:bg-green-100 hover:border-green-300' 
                        : 'hover:bg-gray-100'
                  }`}
                  style={{
                    left: colIndex * cellSize,
                    top: rowIndex * cellSize,
                    width: cellSize,
                    height: cellSize
                  }}
                  onClick={() => handleCellClick(cell)}
                >
                  {cell.occupied && (
                    <div className="w-full h-full flex items-center justify-center text-2xl">
                      {(() => {
                        const object = objects.find(obj => obj.id === cell.objectId);
                        if (object?.type === 'star') return '⭐';
                        if (object?.type === 'circle') return '🔴';
                        if (object?.type === 'square') return '🔵';
                        if (object?.type === 'triangle') return '🟢';
                        if (object?.type === 'diamond') return '💎';
                        if (object?.type === 'hexagon') return '⬡';
                        return '●';
                      })()}
                    </div>
                  )}
                  {showGrid && (
                    <div className="absolute top-1 left-1 text-xs text-gray-400">
                      {cell.position}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        {/* Objects Panel - Now below the grid */}
        <div className="mb-6">
          <h4 className="font-medium text-gray-900 mb-3">Objets à placer:</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {objects.map((object) => (
              <div
                key={object.id}
                className={`p-3 border-2 rounded-lg cursor-pointer transition-all ${
                  selectedObject?.id === object.id
                    ? 'border-blue-500 bg-blue-50'
                    : object.isPlaced
                      ? 'border-green-300 bg-green-50 opacity-60'
                      : 'border-gray-200 hover:border-gray-300'
                }`}
                onClick={() => !object.isPlaced && handleObjectSelect(object)}
              >
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 flex items-center justify-center text-lg">
                    {(() => {
                      if (object.type === 'star') return '⭐';
                      if (object.type === 'circle') return '🔴';
                      if (object.type === 'square') return '🔵';
                      if (object.type === 'triangle') return '🟢';
                      if (object.type === 'diamond') return '💎';
                      if (object.type === 'hexagon') return '⬡';
                      return '●';
                    })()}
                  </div>
                  <div>
                    <div className="font-medium text-gray-900">
                      {object.type.charAt(0).toUpperCase() + object.type.slice(1)} {object.color}
                    </div>
                    <div className="text-sm text-gray-600">
                      Position: {object.position}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        </>
      )}

      {/* Feedback */}
      {showFeedback && (
        <div className={`mt-4 p-3 rounded-lg flex items-center space-x-2 ${
          feedbackType === 'success' 
            ? 'bg-green-100 text-green-800 border border-green-200' 
            : 'bg-red-100 text-red-800 border border-red-200'
        }`}>
          {feedbackType === 'success' ? (
            <CheckCircle className="w-5 h-5" />
          ) : (
            <XCircle className="w-5 h-5" />
          )}
          <span>{feedbackMessage}</span>
        </div>
      )}

      {/* Instructions */}
      <div className="mt-4 p-3 bg-blue-50 rounded-lg">
        <h4 className="font-medium text-blue-900 mb-2">Instructions:</h4>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• Cliquez sur un objet dans la liste ci-dessous pour le sélectionner</li>
          <li>• Cliquez sur une position dans la grille pour placer l'objet sélectionné</li>
          <li>• Suivez les consignes spatiales affichées en haut</li>
          <li>• Utilisez le bouton "Réinitialiser" pour recommencer l'exercice</li>
        </ul>
      </div>
    </div>
  );
};
